/**
 * @file TrackerVisuals.jsx
 * @description Visual building blocks for the provider tracker pages (the landing page and the three
 *   tracker SEO pages). Founder feedback: the pages explained the tracker in paragraphs; show it instead.
 *   - HowItWorksStrip: log a session → pay date is set → late? you're flagged (the reason to sign up).
 *   - IconTiles: icon + short title (+ optional one-line hint) instead of long paragraph cards.
 *   - RoleChips: who it's for, as chips instead of a sentence listing every credential.
 *   - MoreDetails: the long keyword-rich text, collapsed in a native <details> so it stays in the
 *     prerendered HTML for search but nobody has to scroll through it.
 * @module components/TrackerVisuals
 */
import { useTranslation } from "react-i18next";
import { T, typeLabel } from "../theme";
import { ClockIcon, CalendarIcon, AlertTriangleIcon, ChevronDownIcon } from "./Icons";

const NAVY = "#0F1024";
const MID = "#475569";
const BORDER = "rgba(226,232,240,0.9)";

/** Three steps; the last one (late payment flagged) is highlighted in amber. */
export function HowItWorksStrip({ isMobile }) {
  const { t } = useTranslation();
  const steps = [
    { Icon: ClockIcon, title: t("trackerVisual.how1Title"), sub: t("trackerVisual.how1Sub") },
    { Icon: CalendarIcon, title: t("trackerVisual.how2Title"), sub: t("trackerVisual.how2Sub") },
    { Icon: AlertTriangleIcon, title: t("trackerVisual.how3Title"), sub: t("trackerVisual.how3Sub"), warn: true },
  ];
  return (
    <div data-testid="how-it-works" style={{ maxWidth: 920, margin: "0 auto" }}>
      <h2 style={{ textAlign: "center", fontSize: isMobile ? 22 : 28, fontWeight: 800, color: NAVY, letterSpacing: "-0.02em", margin: "0 0 20px" }}>
        {t("trackerVisual.howTitle")}
      </h2>
      <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, minmax(0, 1fr))", gap: isMobile ? 10 : 14 }}>
        {steps.map((s, i) => (
          <li key={s.title} style={{
            display: "flex", flexDirection: isMobile ? "row" : "column", alignItems: "center", gap: isMobile ? 14 : 8,
            textAlign: isMobile ? "left" : "center", padding: isMobile ? "14px 16px" : "22px 18px", borderRadius: 14,
            background: s.warn ? T.orangeLight : "#fff", border: `1.5px solid ${s.warn ? "#FCD34D" : BORDER}`,
          }}>
            <span style={{ width: 44, height: 44, borderRadius: 12, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: s.warn ? "#FDE68A" : T.indigoLight }}>
              <s.Icon size={21} color={s.warn ? T.orange : T.indigo} />
            </span>
            <span>
              <span style={{ display: "block", fontSize: 15, fontWeight: 800, color: s.warn ? "#92400E" : NAVY }}>{i + 1}. {s.title}</span>
              <span style={{ display: "block", fontSize: 13, color: s.warn ? "#92400E" : MID, marginTop: 2 }}>{s.sub}</span>
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

/**
 * @param {{Icon:Function,title:string,hint?:string}[]} props.items
 * @param {number} [props.columns] desktop columns (mobile is always 2)
 */
export function IconTiles({ items, isMobile, columns = 3 }) {
  return (
    <ul data-testid="icon-tiles" style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gridTemplateColumns: `repeat(${isMobile ? 2 : columns}, minmax(0, 1fr))`, gap: isMobile ? 10 : 14 }}>
      {items.map((it) => (
        <li key={it.title} style={{ background: "#fff", border: `1.5px solid ${BORDER}`, borderRadius: 14, padding: isMobile ? "14px 14px" : "18px 18px" }}>
          <span style={{ width: 38, height: 38, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", background: T.indigoLight, marginBottom: 10 }}>
            <it.Icon size={18} color={T.indigo} />
          </span>
          <span style={{ display: "block", fontSize: isMobile ? 14 : 15, fontWeight: 800, color: NAVY, lineHeight: 1.3 }}>{it.title}</span>
          {it.hint && <span style={{ display: "block", fontSize: 12.5, color: MID, marginTop: 3, lineHeight: 1.45 }}>{it.hint}</span>}
        </li>
      ))}
    </ul>
  );
}

/** Who the tracker is for, as chips. */
export function RoleChips() {
  const { t } = useTranslation();
  const chips = [typeLabel("RBT"), typeLabel("BT"), typeLabel("BCABA"), typeLabel("BCBA"), t("trackerVisual.chip1099")];
  return (
    <div data-testid="role-chips" style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center" }}>
      <span style={{ fontSize: 13, color: MID, alignSelf: "center", marginRight: 2 }}>{t("trackerVisual.chipsLabel")}</span>
      {chips.map((c) => (
        <span key={c} style={{ fontSize: 12.5, fontWeight: 700, color: T.indigoDark, background: T.indigoLight, borderRadius: 999, padding: "5px 12px" }}>{c}</span>
      ))}
    </div>
  );
}

/** Collapsed long-form text. Children stay in the DOM (and the prerendered HTML) while closed. */
export function MoreDetails({ summary, children }) {
  return (
    <details data-testid="more-details" style={{ borderTop: `1px solid ${BORDER}`, marginTop: 22, paddingTop: 4 }}>
      <summary style={{ cursor: "pointer", listStyle: "none", display: "flex", alignItems: "center", gap: 8, padding: "12px 0", fontSize: 14.5, fontWeight: 700, color: T.indigo }}>
        <ChevronDownIcon size={16} color={T.indigo} /> {summary}
      </summary>
      <div style={{ paddingBottom: 6 }}>{children}</div>
    </details>
  );
}

/** Title + body pairs for MoreDetails (the text that used to sit in paragraph cards). */
export function DetailList({ items, headingLevel = 3 }) {
  const H = `h${headingLevel}`;
  return (
    <div style={{ display: "grid", gap: 14 }}>
      {items.map((it) => (
        <div key={it.title}>
          <H style={{ fontSize: 15, fontWeight: 800, color: NAVY, margin: "0 0 4px" }}>{it.title}</H>
          <div style={{ fontSize: 14, color: MID, lineHeight: 1.7 }}>{it.body}</div>
        </div>
      ))}
    </div>
  );
}
